import React, { createContext, useState, useContext } from 'react'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import { useQuery, useMutation } from '@tanstack/react-query'

import { getUser, verifyRetry } from 'api/auth'
import {
  login as loginApi,
  register as registerApi,
  logout as logoutApi,
  AUTH,
} from 'api/auth'

import { User } from 'shared'
import { Typography, Box } from '@mui/material'
import {
  addUserInfoToStorage,
  getOnboardingFromStorage,
  getUserInfoFromStorage,
  removeUserInfoFromStorage,
} from 'utils/helpers'

interface AuthContextProps {
  user: User | null
  userId: string
  isUserLoading: boolean
  isUserFetching: boolean
  login: (data: AUTH) => void
  logout: () => void
  resetUser: () => void
  register: (data: AUTH) => void
  loginLoading: boolean
  registerLoading: boolean
  isLoggingOut: boolean
}

const AuthContext = createContext<AuthContextProps>({
  user: null,
  userId: '',
  isUserLoading: true,
  login: () => null,
  logout: () => null,
  resetUser: () => null,
  register: () => null,
  loginLoading: false,
  registerLoading: false,
  isLoggingOut: false,
  isUserFetching: false,
})

const ResendLinkToast = ({ email }: { email: string }) => {
  const verifyRetryMutation = useMutation({
    mutationFn: verifyRetry,
    onSuccess: () => {
      toast.success('Please, check your email')
    },
  })

  const onClick = () => {
    verifyRetryMutation.mutate({ email })
  }

  return (
    <Typography>
      Click{' '}
      <Box
        component="span"
        sx={{ textDecoration: 'underline' }}
        onClick={onClick}
      >
        here
      </Box>{' '}
      to resend a verification link
    </Typography>
  )
}

const MissingUserToast = () => {
  return (
    <Typography>
      No Account found for this user. Please{' '}
      <a target={'_blank'} href={'/sign-up'}>
        sign-up
      </a>{' '}
      with this email to proceed.
    </Typography>
  )
}

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const userIdFromStorage = getUserInfoFromStorage()

  const [userId, setUserId] = useState<string>(userIdFromStorage)

  const {
    data: user = null,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['user'],
    queryFn: getUser,
    enabled: !!userId,
  })

  const loginMutation = useMutation({
    mutationFn: loginApi,
    onSuccess: (data) => {
      const userId = data?.userId
      setUserId(userId)
      addUserInfoToStorage(userId)
    },
    onError: (error: AxiosError<{ error: string; status: number }>, data) => {
      console.log(error)
      if (error.status === 409) {
        toast.error(<MissingUserToast />)
      }
      if (error?.response?.data?.error === 'User is not confirmed.') {
        toast.info(<ResendLinkToast email={data.email} />)
      }
    },
  })
  const registerMutation = useMutation({
    mutationFn: registerApi,
    onSuccess: () => {
      toast.success('Please, check your email')
    },
  })

  const logoutMutation = useMutation({
    mutationFn: logoutApi,
  })

  const login = (data: AUTH) => loginMutation.mutate(data)

  const register = (data: AUTH) => registerMutation.mutate(data)

  const logout = () => {
    const onboarding = getOnboardingFromStorage()
    localStorage.setItem(
      'onboarding',
      JSON.stringify({
        ...onboarding,
        skip: false,
      })
    )
    setUserId('')
    removeUserInfoFromStorage()
    logoutMutation.mutate()
  }

  const resetUser = () => {
    const onboarding = getOnboardingFromStorage()
    localStorage.setItem(
      'onboarding',
      JSON.stringify({
        ...onboarding,
        skip: false,
      })
    )
    setUserId('')
    removeUserInfoFromStorage()
  }

  return (
    <AuthContext.Provider
      value={{
        user: user || null,
        userId,
        isUserLoading: isLoading,
        isUserFetching: isFetching,
        login,
        logout,
        register,
        resetUser,
        isLoggingOut: logoutMutation.isLoading,
        loginLoading: loginMutation?.isLoading,
        registerLoading: registerMutation?.isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)

export default AuthProvider
